/* eslint-disable complexity */
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';
import { Option, Select } from 'carbon-react/lib/components/select';
import Box from 'carbon-react/lib/components/box';
import classNames from 'classnames';

import { ROUTES } from '@futrli/shared-services';

import { PERIOD_TYPE_OPTIONS, PeriodType } from '../../../../core/constants';
import * as styles from './periods-menu.scss';
import {
  SCENARIO_ONBOARDING_STEP,
  SCENARIO_WITH_INVOICES_ONBOARDING_STEP
} from '../../../../forecast/onboarding/const';
import { ForecastType } from '../../../../forecast/onboarding/shared/onboarding-type-select';
import controlWhatYouSee from '../../../onboarding/arrows/control-what-you-see.png';
import { useAnimationLatency, useOnboardingUrlInfo } from '../../../../forecast/onboarding/utils/hooks';
import { usePredictionsInfoContext } from '../../context/predictions-info-context';
import { useDataGridScrollContext } from '../data-grid-scroll-context';

export const PeriodsMenu = () => {
  const { pathname } = useLocation();
  const { periodType, setPeriodType, forecast } = usePredictionsInfoContext();

  const { resetScrollPosition } = useDataGridScrollContext();

  const intl = useIntl();

  const {
    forecastType,
    onboardingStep,
    isOnboardingScenarioRoute,
    isOnboardingScenarioWithInvoicesRoute
  } = useOnboardingUrlInfo();

  const showOnboardingArrow =
    (isOnboardingScenarioRoute &&
      onboardingStep === SCENARIO_ONBOARDING_STEP.SCRATCH_CONTROL_WHAT_YOU_SEE &&
      forecastType === ForecastType.FromScratch) ||
    (isOnboardingScenarioRoute &&
      onboardingStep === SCENARIO_ONBOARDING_STEP.AUTO_PREDICTIONS_WHAT_YOU_SEE &&
      forecastType === ForecastType.WithAutoPredictions) ||
    (isOnboardingScenarioRoute &&
      onboardingStep === SCENARIO_ONBOARDING_STEP.LYA_CONTROL_WHAT_YOU_SEE &&
      forecastType === ForecastType.FromLYA) ||
    (isOnboardingScenarioWithInvoicesRoute &&
      onboardingStep === SCENARIO_WITH_INVOICES_ONBOARDING_STEP.SCRATCH_CONTROL_WHAT_YOU_SEE &&
      forecastType === ForecastType.FromScratch) ||
    (isOnboardingScenarioWithInvoicesRoute &&
      onboardingStep === SCENARIO_WITH_INVOICES_ONBOARDING_STEP.AUTO_PREDICTIONS_WHAT_YOU_SEE &&
      forecastType === ForecastType.WithAutoPredictions) ||
    (isOnboardingScenarioWithInvoicesRoute &&
      onboardingStep === SCENARIO_WITH_INVOICES_ONBOARDING_STEP.LYA_CONTROL_WHAT_YOU_SEE &&
      forecastType === ForecastType.FromLYA);

  const showAnimatedArrow = useAnimationLatency(showOnboardingArrow);

  const handlePeriodChange = async (event: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const period = event.target.value as PeriodType;
    setPeriodType?.(period);
    resetScrollPosition();
  };

  if (pathname.indexOf(ROUTES.INVOICES) !== -1) {
    return null;
  }

  if (forecast?.kind === 'budget') {
    return null;
  }

  return (
    <>
      <div className={styles.container} style={{ marginLeft: 20, position: 'relative' }}>
        {showOnboardingArrow && (
          <img
            alt="onboarding-arrow"
            src={controlWhatYouSee}
            style={{ position: 'absolute', top: 48, right: 130 }}
            className={classNames(styles.fadeInContainer, {
              [styles.fadeIn]: showAnimatedArrow
            })}
          />
        )}
        <div className={styles.titleLabel} style={{ marginRight: 12 }}>
          <FormattedMessage id="common.periods" defaultMessage="Periods" />
          {':'}
        </div>
        <Box width="164px">
          <Select
            autoComplete="off"
            value={periodType ?? ''}
            onChange={handlePeriodChange}
            size="medium"
            aria-label={intl.formatMessage({
              id: 'common.periods',
              defaultMessage: 'Periods'
            })}
          >
            <Option
              value={PERIOD_TYPE_OPTIONS.MONTHLY}
              text={intl.formatMessage({ id: 'common.monthly', defaultMessage: 'Monthly' })}
            />
            <Option
              value={PERIOD_TYPE_OPTIONS.WEEKLY_MONDAY}
              text={intl.formatMessage({ id: 'common.weekly_mon_sun', defaultMessage: 'Weekly (Mon-Sun)' })}
            />
            <Option
              value={PERIOD_TYPE_OPTIONS.WEEKLY_SUNDAY}
              text={intl.formatMessage({ id: 'common.weekly_sun_sat', defaultMessage: 'Weekly (Sun-Sat)' })}
            />
          </Select>
        </Box>
      </div>
    </>
  );
};
